/**
 * HeaderIcons — global stack-header icon cluster (Task 6 / mobile UX batch spec #5).
 *
 * The shared `headerRight` for every screen on the authenticated parent stack (see
 * app/(app)/_layout.tsx): a calendar shortcut, a bright "priority queue" shortcut (danger
 * tint + a live pending-count badge fed by the review queueStore), and the existing
 * screen-aware HelpButton — unchanged behavior, just relocated here (same
 * screenKeyForRoute resolution the stack used directly before this component existed).
 *
 * Theme tokens only. NEVER imports @smartbox/*.
 */
import { Pressable, StyleSheet, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Text } from '../ui';
import { theme } from '../../theme';
import { useQueueStore, selectPendingCount } from '../../review/queueStore';
import { HelpButton } from '../help';
import { screenKeyForRoute } from '../../help';

export interface HeaderIconsProps {
  /** The active Expo Router route name (route.name from the parent Stack's
   * screenOptions), forwarded to the HelpButton via screenKeyForRoute. */
  routeName: string;
}

// Matches HelpButton's own `compact` box (SIZE = 32) so all three icons in the cluster
// sit at the same height.
const TAP_SIZE = 32;
const ICON_SIZE = theme.spacing.xl; // 20

export function HeaderIcons({ routeName }: HeaderIconsProps) {
  const router = useRouter();
  const pendingCount = useQueueStore(selectPendingCount);

  return (
    <View style={styles.row}>
      <Pressable
        onPress={() => router.push('/(app)/calendar')}
        accessibilityRole="button"
        accessibilityLabel="Open calendar"
        hitSlop={10}
        style={({ pressed }) => [styles.tap, pressed && styles.pressed]}
      >
        <Ionicons name="calendar-outline" size={ICON_SIZE} color={theme.color.action.primary} />
      </Pressable>

      <Pressable
        onPress={() => router.push('/(app)/queue')}
        accessibilityRole="button"
        accessibilityLabel={`Priority queue, ${pendingCount} pending`}
        hitSlop={10}
        style={({ pressed }) => [styles.tap, pressed && styles.pressed]}
      >
        <Ionicons name="alert-circle" size={ICON_SIZE} color={theme.color.action.danger} />
        {pendingCount > 0 ? (
          <View style={styles.badge}>
            <Text variant="small" color="onAccent">
              {pendingCount}
            </Text>
          </View>
        ) : null}
      </Pressable>

      <HelpButton
        compact
        screenKey={screenKeyForRoute(routeName)}
        onOpenHelpCenter={() => router.push('/(app)/help')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  tap: {
    width: TAP_SIZE,
    height: TAP_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: { opacity: 0.6 },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: theme.spacing.xl,
    height: theme.spacing.xl,
    borderRadius: theme.radius.pill,
    backgroundColor: theme.color.action.danger,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xs,
  },
});
